import { type MetaFunction } from '@remix-run/node'
import { Link } from '@remix-run/react'

export const meta: MetaFunction = () => [
	{ title: 'Barry McGee — Agentic Software Engineer' },
	{
		name: 'description',
		content:
			'IC at Shopify. Twenty years shipping web products — now building agentic features into the platform millions of merchants depend on, with AI in the loop.',
	},
	{
		property: 'og:title',
		content: 'Barry McGee — Agentic Software Engineer',
	},
	{
		property: 'og:description',
		content:
			'IC at Shopify. Twenty years shipping web products, now with agents in the loop.',
	},
	{ property: 'og:image', content: 'https://barrymcgee.dev/img/speaking.jpeg' },
	{ property: 'og:type', content: 'website' },
	{ property: 'og:url', content: 'https://barrymcgee.dev' },
	{ name: 'twitter:card', content: 'summary_large_image' },
	{ name: 'twitter:site', content: '@barrymcgee' },
	{ name: 'twitter:creator', content: '@barrymcgee' },
]

const logoWall: Array<{ name: string; node: React.ReactNode }> = [
	{ name: 'Shopify', node: <ShopifyLogo className="h-7 w-auto md:h-9" /> },
	{
		name: 'Canonical',
		node: (
			<img
				src="/img/logos/canonical.svg"
				alt="Canonical"
				className="h-auto w-auto max-w-[110px] translate-y-[8px] md:max-w-[140px] md:translate-y-[10px]"
			/>
		),
	},
	{ name: 'Kahoot!', node: <KahootLogo className="h-7 w-auto md:h-9" /> },
	{
		name: 'FutureLearn',
		node: <FutureLearnLogo className="h-7 w-auto md:h-9" />,
	},
	{
		name: 'Paul Smith',
		node: (
			<img
				src="/img/logos/paul-smith.svg"
				alt="Paul Smith"
				className="h-7 w-auto max-w-[110px] translate-y-[6px] md:h-9 md:max-w-[140px] md:translate-y-2"
			/>
		),
	},
	{ name: 'BBC', node: <BBCLogo className="h-7 w-auto md:h-9" /> },
]

export default function Index() {
	return (
		<main className="container flex-1 p-0">
			<Hero />
			<CurrentlyAtShopify />
			<LogoWall />
			<Closing />
		</main>
	)
}

function Hero() {
	return (
		<section className="relative py-8 md:py-12">
			<div className="grid gap-10 md:grid-cols-2 md:items-center md:gap-10 lg:gap-14">
				<div className="motion-safe:duration-700 motion-safe:animate-in motion-safe:fade-in motion-safe:slide-in-from-bottom-4">
					<h1 className="text-4xl font-bold leading-[1.1] tracking-tight text-foreground sm:text-5xl md:text-[2rem] md:leading-[1.1] lg:text-[2.5rem] xl:text-5xl">
						<span className="block text-muted-foreground">
							Two decades shipping software.
						</span>
						<span className="block">
							Now I ship it
							<span className="relative inline-block px-1">
								<span className="absolute inset-x-0 bottom-0.5 -z-10 h-2 bg-emerald-300/70 md:bottom-1 md:h-2.5 lg:h-3" />
								with agents
							</span>
							.
						</span>
					</h1>
					<div className="mt-6 max-w-xl text-base leading-relaxed text-muted-foreground md:text-lg">
						I’m an IC at <Strong>Shopify</Strong>, building agentic features
						into products that millions of merchants depend on — and shipping at
						multiples of the pace with AI in the loop.
					</div>
					<div className="mt-8 flex flex-wrap items-center gap-3">
						<Link
							to="/contact"
							className="inline-flex items-center gap-2 rounded-full bg-foreground px-6 py-3 font-medium text-background no-underline transition-transform hover:-translate-y-0.5 hover:opacity-90"
						>
							Get in touch
							<span aria-hidden>→</span>
						</Link>
					</div>
				</div>
				<div className="[animation-fill-mode:backwards] motion-safe:delay-200 motion-safe:duration-700 motion-safe:animate-in motion-safe:fade-in motion-safe:slide-in-from-bottom-4">
					<StatusTerminalPanel />
				</div>
			</div>
		</section>
	)
}

function Strong({ children }: { children: React.ReactNode }) {
	return <span className="font-semibold text-foreground">{children}</span>
}

function CurrentlyAtShopify() {
	return (
		<section className="my-12 md:my-16">
			<CurrentlyAtShopifyPanel />
		</section>
	)
}

function CurrentlyAtShopifyPanel() {
	return (
		<div className="overflow-hidden rounded-2xl border-2 border-foreground/10 bg-background shadow-sm">
			<div className="border-b border-foreground/10 bg-foreground/[0.02] px-6 py-3 md:px-10">
				<div className="flex items-center gap-2 font-mono text-xs uppercase tracking-wider text-muted-foreground">
					<span className="relative flex h-2 w-2">
						<span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-emerald-400 opacity-60" />
						<span className="relative inline-flex h-2 w-2 rounded-full bg-emerald-500" />
					</span>
					Currently · since July 2021
				</div>
			</div>
			<div className="grid gap-8 px-6 py-10 md:grid-cols-[auto_1fr] md:gap-12 md:px-10 md:py-12">
				<div className="flex flex-col items-start gap-4 md:items-center md:border-r md:border-foreground/10 md:pr-12">
					<ShopifyMark className="h-14 w-auto shrink-0 md:h-20" />
					<div className="font-mono text-xs uppercase tracking-wider text-muted-foreground md:text-center">
						Shopify
					</div>
				</div>
				<div>
					<h2 className="text-2xl font-bold leading-tight tracking-tight text-foreground md:text-3xl">
						IC at Shopify
					</h2>
					<p className="mt-4 text-base leading-relaxed text-muted-foreground md:text-lg">
						Shopify is the commerce platform behind millions of businesses
						worldwide — from solo creators to global brands selling across web,
						mobile, retail and social.
					</p>
					<div className="mt-6 flex flex-wrap gap-2">
						{['TypeScript', 'React', 'Remix', 'Ruby', 'React Native', 'AI'].map(
							t => (
								<span
									key={t}
									className="rounded-full border border-foreground/10 bg-background px-2.5 py-1 font-mono text-xs text-muted-foreground"
								>
									{t}
								</span>
							),
						)}
					</div>
				</div>
			</div>
		</div>
	)
}

function StatusTerminalPanel() {
	return (
		<div className="overflow-hidden rounded-2xl border border-foreground/10 bg-foreground text-background shadow-2xl">
			<div className="flex items-center gap-1.5 border-b border-background/10 bg-background/5 px-4 py-3">
				<div className="h-3 w-3 rounded-full bg-red-400/80" />
				<div className="h-3 w-3 rounded-full bg-yellow-400/80" />
				<div className="h-3 w-3 rounded-full bg-emerald-400/80" />
				<div className="ml-4 font-mono text-xs text-background/50">
					barry@belfast — zsh
				</div>
			</div>
			<div className="space-y-2 px-5 py-7 font-mono text-xs leading-relaxed md:px-8 md:py-10 md:text-sm">
				<div className="text-background/60">
					<span className="text-emerald-400">~</span> $ barry --whoami
				</div>
				<TerminalLine label="role     " value="IC at Shopify" />
				<TerminalLine
					label="based    "
					value="Belfast, NI · fully remote since 2016"
				/>
				<TerminalLine
					label="stack    "
					value="TypeScript · React · Remix · Ruby · React Native"
				/>
				<TerminalLine
					label="agents   "
					value="multiple harnesses, in the loop daily"
				/>
				<TerminalLine
					label="writes   "
					value="agentic engineering · design systems · senior IC craft"
				/>
				<TerminalLine
					label="talks at "
					value="conferences, meetups, your team"
				/>
				<div className="pt-4 text-background/60">
					<span className="text-emerald-400">~</span> ${' '}
					<span className="inline-block h-3.5 w-2 translate-y-0.5 animate-caret-blink bg-background/80 align-middle" />
				</div>
			</div>
		</div>
	)
}

function TerminalLine({ label, value }: { label: string; value: string }) {
	return (
		<div className="flex flex-wrap items-baseline gap-2">
			<span className="text-emerald-400">{label}</span>
			<span className="text-background/40">→</span>
			<span className="text-background">{value}</span>
		</div>
	)
}

function LogoWall() {
	return (
		<section className="my-16 md:my-24">
			<SectionHeader
				eyebrow="Career so far"
				title="Products that mattered. Two decades of them."
			/>
			<div className="grid grid-cols-2 gap-px overflow-hidden rounded-2xl border border-foreground/10 bg-foreground/10 sm:grid-cols-3 md:grid-cols-6">
				{logoWall.map(logo => (
					<div
						key={logo.name}
						aria-label={logo.name}
						className="group flex items-center justify-center bg-background px-4 py-8 text-muted-foreground transition-colors duration-300 hover:text-foreground md:py-12 [&_svg]:fill-current"
					>
						<div className="opacity-70 grayscale transition-all duration-300 group-hover:opacity-100 group-hover:grayscale-0">
							{logo.node}
						</div>
					</div>
				))}
			</div>
			<p className="mt-5 font-mono text-xs uppercase tracking-wider text-muted-foreground">
				Full timeline →{' '}
				<Link to="/experience" className="text-foreground underline">
					/experience
				</Link>
			</p>
		</section>
	)
}

function Closing() {
	return (
		<section className="my-16 rounded-2xl border border-foreground/10 bg-foreground/[0.03] px-6 py-12 md:my-24 md:px-10 md:py-16">
			<div className="font-mono text-xs uppercase tracking-wider text-muted-foreground">
				Let’s talk
			</div>
			<h2 className="mt-3 text-3xl font-bold tracking-tight text-foreground md:text-4xl">
				Working on something the right kind of hard?
			</h2>
			<p className="mt-5 text-base leading-relaxed text-muted-foreground md:text-lg">
				Happy to talk about agentic engineering, advisory work, speaking, or
				anything else you think I’ll find interesting.
			</p>
			<div className="mt-8 flex flex-wrap items-center gap-3">
				<Link
					to="/contact"
					className="inline-flex items-center gap-2 rounded-full bg-foreground px-5 py-2.5 text-sm font-medium text-background no-underline transition-transform hover:-translate-y-0.5 hover:opacity-90"
				>
					Send a message
					<span aria-hidden>→</span>
				</Link>
				<a
					href="https://linkedin.com/in/barrymcgee"
					target="_blank"
					rel="noreferrer noopener"
					className="inline-flex items-center gap-2 rounded-full border border-foreground/20 px-5 py-2.5 text-sm font-medium text-foreground no-underline transition-colors hover:border-foreground/60 hover:bg-foreground/5"
				>
					LinkedIn
				</a>
			</div>
		</section>
	)
}

function SectionHeader({
	eyebrow,
	title,
}: {
	eyebrow: string
	title?: string
}) {
	return (
		<header className="mb-8 md:mb-10">
			<div className="font-mono text-xs uppercase tracking-wider text-muted-foreground">
				{eyebrow}
			</div>
			{title ? (
				<h2 className="mt-2 text-2xl font-bold tracking-tight text-foreground md:text-3xl">
					{title}
				</h2>
			) : null}
		</header>
	)
}

function ShopifyLogo({ className }: { className?: string }) {
	return (
		<svg
			viewBox="0 0 215 61"
			fill="none"
			xmlns="http://www.w3.org/2000/svg"
			aria-label="Shopify"
			className={className}
		>
			<path
				d="M46.3758 11.5534c-.0416-.303-.3063-.4711-.526-.4894-.218-.0183-4.4894-.0832-4.4894-.0832S37.7881 7.5118 37.4352 7.1589c-.3528-.353-1.042-.2464-1.31-.1665-.0033.0017-.6708.2081-1.7944.556-.1881-.6093-.4645-1.3583-.859-2.1107-1.2717-2.427-3.1344-3.7104-5.385-3.7138-.0033 0-.005 0-.0083 0-.1565 0-.3113.015-.4677.0283-.0666-.0799-.1332-.1581-.2031-.2347C25.9272.4689 24.6705-.0421 23.164.0028 20.2576.086 17.3629 2.185 15.0158 5.9138c-1.6513 2.6234-2.9081 5.9194-3.2643 8.4712-3.3375 1.0337-5.6713 1.7561-5.7229 1.7728C4.31 16.7228 4.2567 16.7744 4.037 18.3626 3.8739 19.5627-.5341 53.6471-.5341 53.6471l36.93923 6.3887 16.0102-3.9801s-6.5319-44.1635-6.5735-44.4665ZM32.4814 8.121c-.8506.263-1.8178.5626-2.8665.8872-.0216-1.4715-.1964-3.5189-.8822-5.2884 2.2056.4178 3.2909 2.913 3.7487 4.4012ZM27.6823 9.6075c-1.9359.5992-4.0483 1.2534-6.1673 1.9093.5959-2.2822 1.7262-4.5544 3.1145-6.0442.516-.5543 1.2384-1.1719 2.094-1.5248.804 1.6779.9788 4.0533.9588 5.6597ZM23.7222 1.937c.6825-.015 1.2568.135 1.7479.4579-.7857.4078-1.5448.9938-2.2572 1.7578-1.8461 1.9809-3.261 5.0554-3.8253 8.0217-1.7595.5443-3.4807 1.0786-5.0654 1.568 1.0005-4.6692 4.9139-11.6755 9.4-11.8054Z"
				fill="#95BF47"
			/>
			<path
				d="M45.8514 11.0657c-.218-.0183-4.4894-.0832-4.4894-.0832S37.7898 7.5134 37.4369 7.1605c-.1315-.1315-.3096-.1998-.4961-.228V60.0001L52.951 56.02s-6.5319-44.162-6.5735-44.465c-.0416-.3029-.3083-.4711-.5261-.4893Z"
				fill="#5E8E3E"
			/>
			<path
				d="M28.0668 19.2972 26.2075 26.2535s-2.0741-.9438-4.5327-.789c-3.6056.2281-3.6439 2.5019-3.6072 3.0729.1964 3.1111 8.3812 3.7903 8.8407 11.0779.3612 5.7329-3.0413 9.6547-7.9435 9.9643-5.8844.3712-9.12369-3.0995-9.12369-3.0995L11.0879 41.1767s3.2609 2.4603 5.871 2.2955c1.7046-.1082 2.3138-1.4948 2.2522-2.4753-.2563-4.0583-6.9214-3.8186-7.3425-10.487-.3546-5.6113 3.3309-11.2976 11.4624-11.8103 3.1328-.2014 4.7358.5976 4.7358.5976Z"
				fill="#fff"
			/>
			<path
				d="M74.0322 33.8637c-1.8409-.9989-2.787-1.8409-2.787-2.9983 0-1.4727 1.3142-2.4188 3.3665-2.4188 2.389 0 4.5221.9989 4.5221.9989l1.6824-5.1545s-1.547-1.2102-6.1005-1.2102c-6.3367 0-10.7284 3.6289-10.7284 8.7306 0 2.8926 2.0506 5.1017 4.7863 6.6784 2.2091 1.2614 2.9983 2.1563 2.9983 3.4705 0 1.3671-1.1045 2.4716-3.1551 2.4716-3.0561 0-5.9421-1.5784-5.9421-1.5784L60.8867 48.008s2.6664 1.7881 7.1522 1.7881c6.5216 0 11.2023-3.208 11.2023-8.9932 0-3.1006-2.3659-5.3097-5.2073-6.9392ZM100.013 23.0296c-3.2082 0-5.7326 1.5256-7.6775 3.8387l-.1057-.0529 2.7869-14.567H87.7588L80.7122 49.3239h7.2579l2.4188-12.6733c.946-4.7863 3.4176-7.7301 5.7324-7.7301 1.6295 0 2.2619 1.1045 2.2619 2.6829 0 .9989-.1057 2.2091-.3154 3.208l-2.7341 14.5142h7.2579l2.839-14.9881c.316-1.5784.527-3.4704.527-4.7335 0-4.1028-2.156-6.5744-5.942-6.5744ZM122.363 23.0296c-8.731 0-14.514 7.8887-14.514 16.6705 0 5.6267 3.47 10.1489 9.992 10.1489 8.572 0 14.357-7.6773 14.357-16.6705 0-5.2057-3.051-10.1489-9.835-10.1489ZM118.787 44.2767c-2.472 0-3.524-2.1034-3.524-4.7335 0-4.154 2.157-10.938 6.101-10.938 2.577 0 3.417 2.209 3.417 4.3653 0 4.4693-2.154 11.3062-5.994 11.3062ZM150.762 23.0296c-4.899 0-7.679 4.3125-7.679 4.3125h-.104l.421-3.8915h-6.416c-.315 2.6301-.895 6.6256-1.472 9.6239l-5.049 26.5568h7.258l1.997-10.7284h.159s1.489.9461 4.26.9461c8.519 0 14.093-8.729 14.093-17.5653 0-4.8887-2.157-9.2541-7.468-9.2541ZM143.82 44.3807c-1.884 0-2.999-1.0517-2.999-1.0517l1.21-6.784c.842-4.5222 3.208-7.5205 5.733-7.5205 2.209 0 2.892 2.0506 2.892 3.9972 0 4.6806-2.787 11.359-6.836 11.359ZM168.59 12.6166c-2.313 0-4.154 1.8409-4.154 4.2068 0 2.1563 1.367 3.629 3.418 3.629h.105c2.262 0 4.207-1.5256 4.26-4.2068 0-2.1035-1.42-3.629-3.629-3.629zM158.441 49.3239h7.256l4.945-25.7148h-7.311l-4.89 25.7148ZM189.101 23.5563h-5.049l.262-1.2102c.421-2.4716 1.894-4.6807 4.313-4.6807 1.291 0 2.313.3682 2.313.3682l1.42-5.6795s-1.262-.6307-3.945-.6307c-2.577 0-5.152.7363-7.099 2.4187-2.472 2.1035-3.629 5.1529-4.207 8.204l-.209 1.2102h-3.367L172.481 29.0262h3.367l-3.839 20.2994h7.258l3.839-20.2994h4.996L189.101 23.5563ZM206.56 23.6092s-4.537 11.43-6.574 17.6693h-.106c-.138-2.0093-1.788-17.6693-1.788-17.6693h-7.626L194.833 47.2205c.106.5251.053.8421-.158 1.2102-.843 1.6296-2.262 3.208-3.945 4.3654-1.367.9988-2.892 1.6295-4.101 2.0505L188.627 61c1.473-.3153 4.522-1.5255 7.099-3.9443 3.314-3.1023 6.363-7.8886 9.518-14.4102L214.132 23.6075h-7.572V23.6092Z"
				fill="currentColor"
			/>
		</svg>
	)
}

function KahootLogo({ className }: { className?: string }) {
	return (
		<svg
			viewBox="0 0 250 87"
			xmlns="http://www.w3.org/2000/svg"
			aria-label="Kahoot!"
			className={className}
		>
			<g fill="currentColor" fillRule="evenodd">
				<path d="M128.836 30.126c-10.641.187-19.79 9.218-20.435 20.168-.645 10.95 7.983 20.74 19.27 21.866 11.29 1.122 20.438-7.91 20.438-20.17-.002-12.265-8.628-22.053-19.273-21.864zm1.324 31.695c-5.853.689-10.837-4.663-10.5-11.477.332-6.817 3.058-11.077 8.577-11.193 5.519-.117 8.858 6.221 8.229 13.63-.483 5.673-3.997 8.767-6.306 9.04zM150.174 53.109c0 12.263 9.147 21.294 20.437 20.17 11.288-1.124 19.916-10.914 19.273-21.865-.644-10.95-9.796-19.983-20.44-20.17-10.642-.187-19.27 9.603-19.27 21.865zm11.643.792c-.63-7.41 2.71-13.746 8.229-13.63 5.52.114 8.244 4.374 8.578 11.19.336 6.814-4.65 12.165-10.502 11.476-2.309-.27-5.82-3.364-6.305-9.036zM0 77.59V14.634l13.189-3.384-.033 26.82L35.43 16.59l11.455 4.402L30.697 38.95l10.736 39.111H29.786l-8.591-29.966-8.115 7.947.109 22.02L0 77.59zM90.824 50.13c-.003-.11.008.135 0 0 .008.13-.003-.115 0 0z" />
				<path d="M90.824 50.13c-.003-.09.008.091 0 0 .008.09-.003-.094 0 0z" />
				<path d="M90.824 50.13c-.01-.235.016.292 0 0 .016.269-.011-.254 0 0z" />
				<path d="M96.313 73.195h11.58l-4.355-30.825c-1.647-18.004-8.214-18.046-20.47-14.13L82.816 0 71.544 3.011l1.796 69.8 11.07.382-.64-36.065c2.425-1.073 10.442-3.33 10.71 5.559l1.073 12.678.76 17.83zM235.931 73.557l-3.269 7.699 6.92 4.83 6.823-4.08-2.874-8.449h-7.6zM187.803 25.628l1.897 13.1h7.928l.55 31.157s-1.24 11.21 15.652 8.644c0 0 5.286-1.584 5.118-5.323v-12.31s-3.559 2.129-7.427 2.129c-3.868 0-3.893-1.888-3.893-1.888l-1.034-24.131 12.354-.69v-7.929l-12.648-.834.154-16.33-8.798-3.647-.011 18.395-9.842-.343zM226.687 6.887L250 11.377l-9 57.806-14.313-62.296zM44.084 28.656l3.088 8.151c7.66-4.652 11.988.02 11.988.02l-.059 5.202c-22.826 4.045-17.051 25.4-17.051 25.4 2.309 8.38 9.824 8.09 9.824 8.09l17.115.002.324-38.857c-2.681-18.088-25.229-8.008-25.229-8.008zm14.992 39.43s-8.278 2.027-9.192-6.13c0 0 .177-12.624 9.503-11.267l-.31 17.397z" />
			</g>
		</svg>
	)
}

function FutureLearnLogo({ className }: { className?: string }) {
	return (
		<svg
			viewBox="135 179 920 324"
			xmlns="http://www.w3.org/2000/svg"
			aria-label="FutureLearn"
			className={className}
		>
			<g fill="currentColor">
				<path d="M547.5 234.1H600v21.6h-52.5v57.5h-22.9V179.6h79.2v21.6h-56.3v32.9zM651.4 315.5c-4.3 0-8.5-.8-12.5-2.3-4-1.5-7.5-3.8-10.6-6.7-3.1-2.9-5.5-6.5-7.3-10.7-1.9-4.2-2.8-9-2.8-14.5v-58.6h22.9V275c0 6.7 1.5 11.6 4.6 14.6 3.1 3 7.5 4.5 13.4 4.5 2.9 0 5.5-.6 7.7-1.7s4.1-2.4 5.6-3.8c1.8-1.5 3.3-3.4 4.6-5.5v-60.3h22.9v90.5h-21.4V300c-1.9 2.9-4.1 5.5-6.7 7.8-2.2 1.9-4.9 3.7-8.4 5.2-3.4 1.7-7.4 2.5-12 2.5zM715 222.7h17.7v-26.9h22.9v26.9H783v20.6h-27.3v36.5c0 2.8.3 5.1 1 7 .6 1.8 1.5 3.3 2.7 4.4 1.1 1.1 2.4 1.8 3.8 2.3 1.4.4 2.9.7 4.4.7 1.3 0 2.5-.2 3.6-.5s2.2-.7 3.1-1.1c1-.5 2-1.1 2.9-1.7l8.4 18.7c-1.6 1.1-3.6 2.2-5.9 3.1-1.9.8-4.2 1.4-6.8 2-2.7.6-5.7.9-9 .9-4.5 0-8.6-.8-12.3-2.3-3.7-1.5-7-3.7-9.7-6.5-2.7-2.8-4.9-6.2-6.5-10.1-1.6-3.9-2.4-8.4-2.4-13.2v-40.1h-18v-20.7zM832.5 315.5c-4.3 0-8.5-.8-12.5-2.3-4-1.5-7.5-3.8-10.6-6.7-3-2.9-5.5-6.5-7.3-10.7-1.8-4.2-2.8-9-2.8-14.5v-58.6h22.9V275c0 6.7 1.5 11.6 4.6 14.6 3.1 3 7.5 4.5 13.4 4.5 2.9 0 5.5-.6 7.7-1.7s4.1-2.4 5.6-3.8c1.8-1.5 3.3-3.4 4.6-5.5v-60.3H881v90.5h-21.4V300c-1.9 2.9-4.1 5.5-6.7 7.8-2.1 1.9-4.9 3.7-8.4 5.2-3.4 1.7-7.4 2.5-12 2.5zM948.3 220.4c2 0 4 .2 5.9.5 1.9.3 3.6.7 5 1.1 1.7.5 3.2 1 4.8 1.3l-8 20.6c-.8-.3-1.7-.6-2.9-1-1-.2-2.2-.5-3.5-.8-1.3-.3-2.8-.4-4.5-.4-3.6 0-6.6.7-9.1 1.9-2.5 1.3-4.6 2.7-6.2 4.2-1.9 1.8-3.6 3.8-5 6.1v59.2h-22.9v-90.5h21.8v13.2c1.7-2.8 3.6-5.4 5.9-7.6 2-1.9 4.6-3.7 7.7-5.3 3.1-1.7 6.8-2.5 11-2.5zM1009.7 220.4c6.2 0 12.1 1.2 17.7 3.4 5.5 2.3 10.3 5.5 14.5 9.6 4.1 4.1 7.4 9.1 9.8 14.9 2.4 5.8 3.6 12.2 3.6 19.2v3.6c0 1.5-.1 3.2-.2 4.9h-67.6c.4 2 1.3 4.2 2.7 6.3 1.4 2.2 3.2 4.1 5.3 5.9 2.1 1.8 4.7 3.3 7.6 4.4 2.9 1.2 6.2 1.7 9.7 1.7 3.9 0 7.4-.5 10.5-1.4 3.1-.9 5.7-2 7.8-3.1 2.4-1.4 4.6-2.9 6.5-4.6l11.5 17.2c-2.9 2.4-6.2 4.6-9.9 6.5-3.2 1.7-7 3.1-11.5 4.5-4.4 1.3-9.4 2-14.9 2-6.9 0-13.3-1.2-19.2-3.7-5.9-2.5-11.1-5.9-15.5-10.1-4.4-4.3-7.8-9.3-10.3-15.1-2.5-5.8-3.7-12-3.7-18.6 0-6.6 1.1-12.8 3.4-18.6 2.3-5.8 5.4-10.8 9.4-15.1 4-4.3 8.8-7.6 14.4-10.1 5.9-2.5 11.9-3.7 18.4-3.7zm22.7 39.1c-.1-1.9-.7-3.9-1.6-6.1-1-2.2-2.4-4.2-4.2-6-1.8-1.8-4.2-3.3-7-4.5-2.8-1.1-6.1-1.7-9.9-1.7-3.7 0-6.9.6-9.5 1.7-2.7 1.1-4.9 2.6-6.8 4.4-1.8 1.8-3.2 3.8-4.2 5.9-.9 2.1-1.5 4.3-1.6 6.3h44.8zM524.6 360.1h22.9v119.1h56.9v21.6h-79.8V360.1zM656.6 408c6.2 0 12.1 1.1 17.6 3.4 5.5 2.3 10.4 5.5 14.5 9.6s7.4 9.1 9.8 14.9c2.4 5.8 3.6 12.2 3.6 19.2v3.6c0 1.5-.1 3.2-.2 4.9h-67.5c.4 2 1.3 4.1 2.7 6.3 1.4 2.2 3.2 4.1 5.3 5.9 2.1 1.8 4.7 3.2 7.6 4.4 2.9 1.2 6.2 1.7 9.7 1.7 3.9 0 7.4-.5 10.5-1.4 3.1-1 5.7-2 7.8-3.1 2.4-1.4 4.6-2.9 6.5-4.6L696 490c-2.9 2.4-6.2 4.6-9.9 6.5-3.2 1.6-7 3.1-11.4 4.5-4.5 1.3-9.4 2-14.9 2-6.9 0-13.2-1.2-19.2-3.7-5.9-2.5-11.1-5.9-15.4-10.1-4.4-4.3-7.8-9.3-10.3-15.1-2.5-5.8-3.7-12-3.7-18.6 0-6.6 1.1-12.8 3.4-18.6 2.3-5.8 5.4-10.8 9.4-15.1 4-4.3 8.8-7.6 14.4-10.1 5.7-2.5 11.7-3.7 18.2-3.7zm22.7 39.1c-.2-1.9-.7-3.9-1.6-6.1-1-2.2-2.4-4.2-4.2-6-1.9-1.8-4.2-3.3-7-4.5-2.8-1.2-6.1-1.7-9.9-1.7-3.7 0-6.9.6-9.5 1.7-2.7 1.1-4.9 2.6-6.8 4.4s-3.3 3.7-4.2 5.9c-1 2.2-1.5 4.3-1.6 6.3h44.8zM807 410.3v90.4h-21.2v-11.8c-1.7 2.7-3.8 5-6.3 7.1-2.2 1.8-5 3.4-8.5 4.9-3.5 1.5-7.6 2.2-12.5 2.2-5.9 0-11.5-1.2-16.8-3.5-5.3-2.3-10-5.6-14-9.8-4-4.2-7.2-9.2-9.6-15-2.4-5.8-3.6-12.2-3.6-19.2s1.2-13.4 3.6-19.2c2.4-5.8 5.6-10.8 9.6-15 4-4.2 8.7-7.5 14-9.8 5.4-2.3 10.9-3.5 16.8-3.5 4.7 0 8.8.7 12.3 2.2 3.5 1.5 6.3 3.1 8.5 4.9 2.5 2.2 4.7 4.5 6.5 7.1v-11.9H807zM784.1 442c-1.4-2.3-3.1-4.4-5.2-6.3-1.8-1.5-4-3-6.7-4.3-2.7-1.3-5.9-2-9.5-2-3.8 0-7.3.7-10.4 2-3.1 1.3-5.8 3.2-7.9 5.5-2.2 2.4-3.8 5.1-5 8.3-1.2 3.2-1.7 6.6-1.7 10.3 0 3.7.6 7.1 1.7 10.3 1.2 3.2 2.8 5.9 5 8.3 2.1 2.3 4.8 4.2 7.9 5.5 3.1 1.3 6.6 2 10.4 2 3.7 0 6.9-.6 9.5-1.9 2.7-1.3 4.9-2.7 6.7-4.4 2.1-1.8 3.8-3.9 5.2-6.3v-27zM873.4 408c2 0 4 .2 5.9.5 1.9.3 3.6.7 5 1 1.6.5 3.2 1 4.8 1.3l-8 20.6c-.8-.2-1.7-.6-2.9-1-1-.3-2.2-.5-3.5-.8-1.4-.3-2.8-.4-4.5-.4-3.6 0-6.6.6-9.1 1.9-2.5 1.3-4.6 2.7-6.2 4.2-1.9 1.8-3.6 3.8-4.9 6.1v59.1h-23v-90.4h21.7v13.2c1.6-2.8 3.6-5.3 5.9-7.6 2-1.9 4.6-3.7 7.7-5.4 3.2-1.5 6.9-2.3 11.1-2.3zM949.2 408c4.3 0 8.5.8 12.5 2.3 4 1.5 7.5 3.8 10.6 6.7 3.1 2.9 5.5 6.5 7.3 10.7 1.8 4.2 2.8 9 2.8 14.5v58.6h-22.9V448c0-6.5-1.7-11.2-5.2-14.1-3.5-2.9-7.7-4.4-12.7-4.4-2.9 0-5.5.6-7.7 1.9-2.2 1.3-4.1 2.6-5.6 4-1.8 1.6-3.3 3.6-4.6 5.7v59.7h-22.9v-90.4h21.4v13.2c1.7-2.8 3.8-5.3 6.3-7.6 2.2-1.9 5-3.7 8.4-5.4 3.3-1.8 7.4-2.6 12.3-2.6z" />
			</g>
			<path
				fill="#C03A8E"
				d="M446.7 179.6v106.7H344.9v93.8H242v93.7H135.2v27l133.7.1v-93.8h102.9v-93.9h101.9V179.6z"
			/>
		</svg>
	)
}

function BBCLogo({ className }: { className?: string }) {
	return (
		<svg
			viewBox="0 0 248 72"
			xmlns="http://www.w3.org/2000/svg"
			aria-label="BBC"
			className={className}
		>
			<g fill="currentColor" fillRule="evenodd">
				<path d="M134.325 46.055c0 7.665-9.539 7.21-9.539 7.21h-9.538V39.412h9.538c9.823-.056 9.539 6.643 9.539 6.643zm-19.077-27.763h7.268c7.55.398 7.266 6.018 7.266 6.018 0 6.983-8.346 7.097-8.346 7.097h-6.188V18.292zm17.317 15.84s6.529-2.782 6.472-10.163c0 0 .993-12.092-15.031-13.569H106.22v50.7h20.382s17.033.058 17.033-14.307c0 0 .397-9.765-11.071-12.66zM86.545.465h74.862V71.01H86.546V.465zM47.997 46.055c0 7.665-9.539 7.21-9.539 7.21H28.92V39.412h9.538c9.823-.056 9.539 6.643 9.539 6.643zM28.92 18.292h7.268c7.55.398 7.267 6.018 7.267 6.018 0 6.983-8.347 7.097-8.347 7.097H28.92V18.292zm17.317 15.84s6.529-2.782 6.472-10.163c0 0 .993-12.092-15.031-13.569H19.893v50.7h20.383s17.032.058 17.032-14.307c0 0 .398-9.765-11.071-12.66zM.217.465H75.08V71.01H.218V.465zM232.462 14.035v9.368s-9.142-5.621-19.247-5.735c0 0-18.85-.368-19.701 18.07 0 0-.682 16.96 19.474 17.926 0 0 8.46 1.022 19.928-6.302v9.709s-15.386 9.14-33.214 2.1c0 0-14.989-5.477-15.557-23.433 0 0-.625-18.467 19.36-24.599 0 0 5.338-2.044 14.933-1.136 0 0 5.734.568 14.024 4.032zM172.873 71.01h74.861V.465h-74.86V71.01z" />
			</g>
		</svg>
	)
}

function ShopifyMark({ className }: { className?: string }) {
	return (
		<svg
			viewBox="0 0 53 61"
			fill="none"
			xmlns="http://www.w3.org/2000/svg"
			aria-label="Shopify"
			className={className}
		>
			<path
				d="M46.3758 11.5534c-.0416-.303-.3063-.4711-.526-.4894-.218-.0183-4.4894-.0832-4.4894-.0832S37.7881 7.5118 37.4352 7.1589c-.3528-.353-1.042-.2464-1.31-.1665-.0033.0017-.6708.2081-1.7944.556-.1881-.6093-.4645-1.3583-.859-2.1107-1.2717-2.427-3.1344-3.7104-5.385-3.7138-.0033 0-.005 0-.0083 0-.1565 0-.3113.015-.4677.0283-.0666-.0799-.1332-.1581-.2031-.2347-.9805-1.04877-2.2372-1.55981-3.7437-1.51486-2.9064.08323-5.8011 2.18229-8.1482 5.91101-1.6513 2.62341-2.9081 5.91941-3.2643 8.47121-3.3375 1.0337-5.6713 1.7561-5.7229 1.7728-1.6846.5293-1.7378.5809-1.95753 2.169-.16313 1.2001-4.57101 35.2846-4.57101 35.2846l36.93923 6.3887 16.0102-3.9801s-6.5319-44.1635-6.5735-44.4665ZM32.4814 8.121c-.8506.263-1.8178.5626-2.8665.8872-.0216-1.4715-.1964-3.5189-.8822-5.2884 2.2056.4178 3.2909 2.913 3.7487 4.4012ZM27.6823 9.6075c-1.9359.5992-4.0483 1.2534-6.1673 1.9093.5959-2.2822 1.7262-4.5544 3.1145-6.0442.516-.5543 1.2384-1.1719 2.094-1.5248.804 1.6779.9788 4.0533.9588 5.6597ZM23.7222 1.937c.6825-.015 1.2568.135 1.7479.4579-.7857.4078-1.5448.9938-2.2572 1.7578-1.8461 1.9809-3.261 5.0554-3.8253 8.0217-1.7595.5443-3.4807 1.0786-5.0654 1.568 1.0005-4.6692 4.9139-11.6755 9.4-11.8054Z"
				fill="#95BF47"
			/>
			<path
				d="M45.8514 11.0657c-.218-.0183-4.4894-.0832-4.4894-.0832S37.7898 7.5134 37.4369 7.1605c-.1315-.1315-.3096-.1998-.4961-.228V60.0001L52.951 56.02s-6.5319-44.162-6.5735-44.465c-.0416-.3029-.3083-.4711-.5261-.4893Z"
				fill="#5E8E3E"
			/>
			<path
				d="M28.0668 19.2972 26.2075 26.2535s-2.0741-.9438-4.5327-.789c-3.6056.2281-3.6439 2.5019-3.6072 3.0729.1964 3.1111 8.3812 3.7903 8.8407 11.0779.3612 5.7329-3.0413 9.6547-7.9435 9.9643-5.8844.3712-9.12369-3.0995-9.12369-3.0995L11.0879 41.1767s3.2609 2.4603 5.871 2.2955c1.7046-.1082 2.3138-1.4948 2.2522-2.4753-.2563-4.0583-6.9214-3.8186-7.3425-10.487-.3546-5.6113 3.3309-11.2976 11.4624-11.8103 3.1328-.2014 4.7358.5976 4.7358.5976Z"
				fill="#fff"
			/>
		</svg>
	)
}
